import { downloadBlob } from './csv-export.util';

export type ReportExportFormat = 'pdf' | 'xlsx' | 'csv';

/**
 * Client-side PDF report renderer.
 *
 * Replaces the old layout, which split the page into equal columns, kept every
 * row at a fixed 18pt and drew only the FIRST wrapped line of each cell — long
 * emails were cut mid-address and long Arabic names overprinted their
 * neighbours. The layout now mirrors the server renderer
 * (`functions/functions/API2/lib/reportPdf.js`):
 *
 *   - content-weighted column widths with a floor, not equal columns;
 *   - cells wrap and the row grows to fit, so nothing is truncated;
 *   - Arabic is shaped with arabic-reshaper and reordered with bidi-js (the
 *     pipeline verified codepoint-for-codepoint against python-bidi on
 *     2026-09-15), drawn with the bundled Noto Sans Arabic, and split into
 *     per-script runs so Latin/digits stay on Helvetica (the Arabic font has
 *     no Latin glyphs);
 *   - title + header band repeat on overflow pages, zebra rows, page numbers.
 *
 * jsPDF's own `processArabic` hook is a harmless no-op on our output: it only
 * substitutes base-block codepoints (0x621–0x6D3) and our text is already in
 * the presentation-form blocks, so it passes through untouched (jspdf 4.2.1).
 */

const ARABIC_RE = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;

interface ArabicLibs {
    reshape: (text: string) => string;
    reorder: (text: string) => string;
}

let arabicLibsPromise: Promise<ArabicLibs | null> | null = null;

function loadArabicLibs(): Promise<ArabicLibs | null> {
    arabicLibsPromise ||= (async () => {
        try {
            const [reshaperMod, bidiMod] = await Promise.all([
                import('arabic-reshaper'),
                import('bidi-js')
            ]);
            const reshaper = (reshaperMod as any).ArabicReshaper ?? (reshaperMod as any).default ?? reshaperMod;
            const bidiFactory = (bidiMod as any).default ?? bidiMod;
            const bidi = bidiFactory();
            return {
                reshape: (text: string) => reshaper.convertArabic(text),
                reorder: (text: string) => {
                    const levels = bidi.getEmbeddingLevels(text, 'rtl');
                    return bidi.getReorderedString(text, levels);
                }
            };
        } catch {
            return null;
        }
    })();
    return arabicLibsPromise;
}

interface FontData {
    arabicRegular: string;
    arabicBold: string;
    latinRegular: string;
    latinBold: string;
}

let arabicFontDataPromise: Promise<FontData | null> | null = null;

async function fetchTtfBase64(path: string): Promise<string | null> {
    try {
        const res = await fetch(path);
        return res.ok ? await arrayBufferToBase64(await res.arrayBuffer()) : null;
    } catch {
        return null;
    }
}

function loadFontData(): Promise<FontData | null> {
    arabicFontDataPromise ||= (async () => {
        const [arabicRegular, arabicBold, latinRegular, latinBold] = await Promise.all([
            fetchTtfBase64('assets/fonts/NotoSansArabic-Regular.ttf'),
            fetchTtfBase64('assets/fonts/NotoSansArabic-Bold.ttf'),
            fetchTtfBase64('assets/fonts/DejaVuSans.ttf'),
            fetchTtfBase64('assets/fonts/DejaVuSans-Bold.ttf')
        ]);
        if (!arabicRegular && !latinRegular) return null;
        return {
            arabicRegular: arabicRegular ?? '',
            arabicBold: arabicBold ?? arabicRegular ?? '',
            latinRegular: latinRegular ?? '',
            latinBold: latinBold ?? latinRegular ?? ''
        };
    })();
    return arabicFontDataPromise;
}

function arrayBufferToBase64(buffer: ArrayBuffer): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || '').split(',')[1] || '');
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(new Blob([buffer]));
    });
}

function cellText(value: unknown): string {
    if (value === null || value === undefined) return '';
    if (value instanceof Date) return value.toISOString().slice(0, 10);
    if (typeof value === 'number' && Number.isFinite(value)) {
        return Number.isInteger(value)
            ? value.toLocaleString('en-US')
            : value.toLocaleString('en-US', { maximumFractionDigits: 2 });
    }
    return String(value).replace(/\s+/g, ' ').trim();
}

interface TextRun {
    text: string;
    latin: boolean;
}

/** Split a VISUAL string into per-script runs; spaces ride with the current run. */
function splitRuns(visual: string): TextRun[] {
    const runs: TextRun[] = [];
    let current = '';
    let currentLatin: boolean | null = null;
    for (const ch of visual) {
        const latin = !ARABIC_RE.test(ch);
        if (currentLatin === null) {
            currentLatin = latin;
            current = ch;
        } else if (latin === currentLatin || ch === ' ') {
            current += ch;
        } else {
            runs.push({ text: current, latin: currentLatin });
            currentLatin = latin;
            current = ch;
        }
    }
    if (current) runs.push({ text: current, latin: currentLatin ?? true });
    return runs;
}

/** Greedy logical-order word wrap; an over-wide single word is hard-split. */
function wrapLogical(text: string, maxWidth: number, measure: (candidate: string) => number): string[] {
    const raw = String(text ?? '').replace(/\s+/g, ' ').trim();
    if (!raw) return [];
    const words = raw.split(' ');
    const lines: string[] = [];
    let line = '';
    for (const word of words) {
        const candidate = line ? `${line} ${word}` : word;
        if (measure(candidate) <= maxWidth) {
            line = candidate;
            continue;
        }
        if (!line) {
            // Single word wider than the column: split it by characters.
            let chunk = '';
            for (const ch of word) {
                const chunkCandidate = chunk + ch;
                if (measure(chunkCandidate) <= maxWidth || !chunk) {
                    chunk = chunkCandidate;
                    continue;
                }
                lines.push(chunk);
                chunk = ch;
            }
            line = chunk;
            continue;
        }
        lines.push(line);
        line = word;
    }
    if (line) lines.push(line);
    return lines;
}

const PROFILE_BY_REPORT: ReadonlyArray<{ match: (headers: string[]) => boolean; weights: number[] | null }> = [
    {
        // # UnitID Status BUA Beds Baths Garden plan plan plan Leads
        match: (headers) => headers.includes('Unit ID') && headers.includes('Leads'),
        weights: [3, 10, 12, 6, 6, 7, 7, 9, 9, 9, 22]
    },
    {
        // # UnitID Name Phone Email Status Date Source Notes
        match: (headers) => headers.includes('Full Name') && headers.includes('Email'),
        weights: [3, 8, 14, 10, 16, 9, 8, 8, 20]
    }
];

/** Widths for `headers`: a known profile when one matches, else content-based. */
function columnWidths(headers: string[], rows: string[][], usableWidth: number): number[] {
    const profile = PROFILE_BY_REPORT.find((entry) => entry.match(headers));
    const weights = profile?.weights && profile.weights.length === headers.length
        ? profile.weights
        : autoWeights(headers, rows);
    const total = weights.reduce((sum, w) => sum + w, 0) || 1;
    const widths = weights.map((w) => (w / total) * usableWidth);
    const MIN = 26;
    return widths.map((w) => Math.max(MIN, w));
}

function autoWeights(headers: string[], rows: string[][]): number[] {
    const sample = rows.slice(0, 60);
    return headers.map((header, index) => {
        let longest = header.length;
        for (const row of sample) {
            const len = (row[index] || '').length;
            if (len > longest) longest = len;
        }
        return Math.min(24, Math.max(5, longest * 0.7 + 3));
    });
}

type FontChoice = { family: string; style: 'normal' | 'bold' };
type FontPair = { latin: FontChoice; arabic: FontChoice };

/**
 * Logical-order string → visual-order string for an LTR drawing canvas.
 * Latin-only strings pass through untouched.
 */
function makeVisualizer(libs: ArabicLibs | null): (text: string) => string {
    // Repeated cells (statuses, sources) reshape identically; the memo keeps
    // thousands of rows from re-running the bidi pipeline for every wrap probe.
    const memo = new Map<string, string>();
    return (text: string): string => {
        if (!text || !ARABIC_RE.test(text)) return text;
        const hit = memo.get(text);
        if (hit !== undefined) return hit;
        let visual = text;
        if (libs) {
            visual = libs.reorder(libs.reshape(text));
        }
        if (memo.size > 30000) memo.clear();
        memo.set(text, visual);
        return visual;
    };
}

export async function downloadPdfReport(
    filename: string,
    title: string,
    header: readonly string[],
    rows: readonly (readonly unknown[])[]
): Promise<void> {
    const { jsPDF } = await import('jspdf');
    const headers = header.map((h) => cellText(h));
    const bodyRows = rows.map((row) => headers.map((_h, index) => cellText(row?.[index])));

    const allText = headers.join(' ') + ' ' + bodyRows.map((row) => row.join(' ')).join(' ');
    const hasArabic = ARABIC_RE.test(allText);
    // Latin-Extended names (Ĺ, ł, ĉ …) render as wrong glyphs in the built-in
    // WinAnsi fonts — DejaVu covers them.
    const hasNonWinAnsi = /[\u0100-\u024F\u1E00-\u1EFF]/.test(allText);
    const libs = hasArabic ? await loadArabicLibs() : null;
    const fontData = hasArabic || hasNonWinAnsi ? await loadFontData() : null;
    const toVisual = makeVisualizer(libs);

    const orientation = headers.length > 6 ? 'landscape' : 'portrait';
    const doc = new jsPDF({ orientation, unit: 'pt', format: 'a4', compress: true });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 26;
    const fontSize = 7;
    const lineHeight = fontSize + 3;
    const padY = 4;
    const padX = 4;
    const usableWidth = pageWidth - margin * 2;

    const FONTS: FontPair = { latin: { family: 'helvetica', style: 'normal' }, arabic: { family: 'helvetica', style: 'normal' } };
    const FONTS_BOLD: FontPair = { latin: { family: 'helvetica', style: 'bold' }, arabic: { family: 'helvetica', style: 'bold' } };
    if (fontData) {
        if (fontData.latinRegular) {
            doc.addFileToVFS('DejaVuSans.ttf', fontData.latinRegular);
            doc.addFont('DejaVuSans.ttf', 'DejaVu', 'normal');
            FONTS.latin = { family: 'DejaVu', style: 'normal' };
        }
        if (fontData.latinBold) {
            doc.addFileToVFS('DejaVuSans-Bold.ttf', fontData.latinBold);
            doc.addFont('DejaVuSans-Bold.ttf', 'DejaVu', 'bold');
            FONTS_BOLD.latin = { family: 'DejaVu', style: 'bold' };
        }
        if (fontData.arabicRegular) {
            doc.addFileToVFS('NotoSansArabic-Regular.ttf', fontData.arabicRegular);
            doc.addFont('NotoSansArabic-Regular.ttf', 'NotoSansArabic', 'normal');
            FONTS.arabic = { family: 'NotoSansArabic', style: 'normal' };
        }
        if (fontData.arabicBold) {
            doc.addFileToVFS('NotoSansArabic-Bold.ttf', fontData.arabicBold);
            doc.addFont('NotoSansArabic-Bold.ttf', 'NotoSansArabic', 'bold');
            FONTS_BOLD.arabic = { family: 'NotoSansArabic', style: 'bold' };
        }
    }

    const TITLE_INK: [number, number, number] = [25, 35, 42];
    const INK: [number, number, number] = [35, 43, 48];
    const MUTED: [number, number, number] = [90, 98, 105];
    const HEADER_BAND: [number, number, number] = [67, 67, 67];
    const ZEBRA: [number, number, number] = [247, 248, 249];
    const GRID: [number, number, number] = [225, 228, 231];

    const widths = columnWidths(headers, bodyRows, usableWidth);
    const colLeft: number[] = [];
    widths.reduce((acc, w) => {
        colLeft.push(acc);
        return acc + w;
    }, margin);

    const setFont = (font: FontChoice): void => {
        doc.setFont(font.family, font.style);
        doc.setFontSize(fontSize);
    };

    const measureRuns = (fonts: FontPair, runs: TextRun[]): number =>
        runs.reduce((sum, run) => {
            const font = run.latin ? fonts.latin : fonts.arabic;
            setFont(font);
            return sum + doc.getTextWidth(run.text);
        }, 0);

    const measureMixed = (fonts: FontPair, text: string): number =>
        measureRuns(fonts, splitRuns(toVisual(text)));

    /** Draw pre-wrapped LOGICAL lines; Arabic-leading lines align right. */
    const drawLines = (lines: string[], x: number, width: number, firstBaseline: number, fonts: FontPair, color: [number, number, number]): void => {
        let baseline = firstBaseline;
        for (const logicalLine of lines) {
            const visual = toVisual(logicalLine);
            const runs = splitRuns(visual);
            const totalWidth = measureRuns(fonts, runs);
            // Arabic-leading visual text hugs the right edge, like the server
            // renderer; neutral runs (digits, punctuation) keep left alignment.
            const rightAlign = Boolean(visual) && ARABIC_RE.test(visual) && ARABIC_RE.test(visual[0]);
            let cursorX = rightAlign ? x + Math.max(0, width - totalWidth) : x;
            doc.setTextColor(color[0], color[1], color[2]);
            for (const run of runs) {
                const font = run.latin ? fonts.latin : fonts.arabic;
                setFont(font);
                doc.text(run.text, cursorX, baseline);
                cursorX += doc.getTextWidth(run.text);
            }
            baseline += lineHeight;
        }
    };

    const wrapCell = (text: string, width: number, fonts: FontPair): string[] =>
        wrapLogical(text, width, (line) => measureMixed(fonts, line));

    let y = margin;

    const drawTitle = (): void => {
        doc.setFont(FONTS_BOLD.latin.family, FONTS_BOLD.latin.style);
        doc.setFontSize(13);
        doc.setTextColor(TITLE_INK[0], TITLE_INK[1], TITLE_INK[2]);
        doc.text(toVisual(title || 'Report'), margin, y + 13);
        y += 23;
    };

    const drawHeaderBand = (): void => {
        const wrappedHeaders = headers.map((h, index) => wrapCell(h, widths[index] - padX * 2, FONTS_BOLD));
        const lineCount = wrappedHeaders.reduce((max, lines) => Math.max(max, lines.length), 1);
        const height = lineCount * lineHeight + padY * 2;
        doc.setFillColor(HEADER_BAND[0], HEADER_BAND[1], HEADER_BAND[2]);
        doc.rect(margin, y, usableWidth, height, 'F');
        headers.forEach((_h, index) => {
            drawLines(wrappedHeaders[index], colLeft[index] + padX, widths[index] - padX * 2, y + padY + fontSize, FONTS_BOLD, [255, 255, 255]);
        });
        y += height;
    };

    drawTitle();
    drawHeaderBand();

    for (let rowIndex = 0; rowIndex < bodyRows.length; rowIndex++) {
        const row = bodyRows[rowIndex];
        const wrapped = headers.map((_h, index) => wrapCell(row[index], widths[index] - padX * 2, FONTS));
        const lineCount = wrapped.reduce((max, lines) => Math.max(max, lines.length), 1);
        const rowHeight = lineCount * lineHeight + padY * 2;

        if (y + rowHeight > pageHeight - margin - 12) {
            doc.addPage();
            y = margin;
            drawTitle();
            drawHeaderBand();
        }

        if (rowIndex % 2 === 1) {
            doc.setFillColor(ZEBRA[0], ZEBRA[1], ZEBRA[2]);
            doc.rect(margin, y, usableWidth, rowHeight, 'F');
        }

        headers.forEach((_h, index) => {
            const lines = wrapped[index];
            if (!lines.length) return;
            drawLines(lines, colLeft[index] + padX, widths[index] - padX * 2, y + padY + fontSize, FONTS, INK);
        });

        y += rowHeight;
        doc.setDrawColor(GRID[0], GRID[1], GRID[2]);
        doc.setLineWidth(0.5);
        doc.line(margin, y, margin + usableWidth, y);
    }

    const totalPages = doc.getNumberOfPages();
    for (let page = 1; page <= totalPages; page++) {
        doc.setPage(page);
        doc.setFont(FONTS.latin.family, FONTS.latin.style);
        doc.setFontSize(7);
        doc.setTextColor(MUTED[0], MUTED[1], MUTED[2]);
        doc.text(`${page} / ${totalPages}`, pageWidth / 2, pageHeight - 12, { align: 'center' });
    }

    downloadBlob(doc.output('blob'), filename);
}
